import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogHeader,
    GridRow,
    Icon,
    Input,
    ToastLevel,
    useIntl,
    useToast,
} from "@metsooutotec/modes-react-components"
import { FC, useEffect, useState } from "react"
import styles from "./user-reset-password-modal.module.css"
import usersStore from "features/users/store/users-store"
import { observer } from "mobx-react-lite"

interface ResetPasswordModalProps {
    isOpen: boolean
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    userId: string
}

const ResetPasswordModal: FC<ResetPasswordModalProps> = observer(({ isOpen, setIsOpen, userId }) => {
    const { formatMessage } = useIntl()
    const { setMessage } = useToast()
    const { userLoading, resetPasswordAsync } = usersStore
    const [password, setPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [errors, setErrors] = useState({ password: "", confirmPassword: "" })
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    useEffect(() => {
        if (isOpen) {
            setPassword("")
            setConfirmPassword("")
            setErrors({ password: "", confirmPassword: "" })
            setShowPassword(false)
            setShowConfirmPassword(false)
        }
    }, [isOpen])

    const getPasswordValidationError = (pwd: string): string => {
        if (pwd.length < 6) return formatMessage("userResetPassword.rule1")
        if (!/\d/.test(pwd)) return formatMessage("userResetPassword.rule2")
        if (!/[a-z]/.test(pwd)) return formatMessage("userResetPassword.rule3")
        if (!/[A-Z]/.test(pwd)) return formatMessage("userResetPassword.rule4")
        return ""
    }

    const validate = (pwd: string, confirmPwd: string) => {
        const passwordError = getPasswordValidationError(pwd)
        const confirmError = !confirmPwd
            ? formatMessage("userResetPassword.confirm")
            : confirmPwd !== pwd
            ? formatMessage("userResetPassword.notMatch")
            : ""

        const result = { password: passwordError, confirmPassword: confirmError }
        setErrors(result)
        return !passwordError && !confirmError
    }

    const handlePasswordChange = (value: string) => {
        setPassword(value)
        validate(value, confirmPassword)
    }

    const handleConfirmPasswordChange = (value: string) => {
        setConfirmPassword(value)
        validate(password, value)
    }

    const handleSubmit = async () => {
        if (!validate(password, confirmPassword)) return

        try {
            const result = await resetPasswordAsync({ id: userId, newPassword: password })
            if (!result.passed) {
                setMessage(formatMessage("userResetPassword.fieldMessage"), ToastLevel.ALARM)
                return
            }
            setMessage(formatMessage("userResetPassword.successMessage"), ToastLevel.SUCCESS)
            setIsOpen(false)
        } catch (e) {
            setMessage(formatMessage("userResetPassword.fieldMessage"), ToastLevel.ALARM)
        }
    }

    return (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} size='tiny'>
            <DialogHeader>{formatMessage("user.resetPassword")}</DialogHeader>
            <DialogContent>
                <GridRow className={styles.rulesList}>
                    <ul className={styles.rules}>
                        <li>{formatMessage("userResetPassword.rule1")}</li>
                        <li>{formatMessage("userResetPassword.rule2")}</li>
                        <li>{formatMessage("userResetPassword.rule3")}</li>
                        <li>{formatMessage("userResetPassword.rule4")}</li>
                    </ul>
                </GridRow>

                <GridRow className={styles.inputGridRow}>
                    <Input
                        id='password'
                        type={showPassword ? "text" : "password"}
                        label={formatMessage("userResetPassword.newPasswordLabel")}
                        placeholder={formatMessage("userResetPassword.newPasswordPlaceholder")}
                        value={password}
                        error={errors.password}
                        onChange={e => handlePasswordChange(e.target.value)}
                        required
                        spanning
                        icon={<Icon className={showPassword ? "icon-eye-closed-16" : "icon-eye-16"} onClick={() => setShowPassword(prev => !prev)} />}
                    />
                </GridRow>

                <GridRow className={styles.inputGridRow}>
                    <Input
                        id='confirmPassword'
                        type={showConfirmPassword ? "text" : "password"}
                        label={formatMessage("userResetPassword.confirmPasswordLabel")}
                        placeholder={formatMessage("userResetPassword.confirmPasswordPlaceholder")}
                        value={confirmPassword}
                        error={errors.confirmPassword}
                        onChange={e => handleConfirmPasswordChange(e.target.value)}
                        required
                        spanning
                        icon={
                            <Icon
                                className={showConfirmPassword ? "icon-eye-closed-16" : "icon-eye-16"}
                                onClick={() => setShowConfirmPassword(prev => !prev)}
                            />
                        }
                    />
                </GridRow>
            </DialogContent>
            <DialogActions>
                <Button children={formatMessage("common.cancel")} onClick={() => setIsOpen(false)} flexible centered />
                <Button
                    children={formatMessage("userResetPassword.resetButton")}
                    onClick={handleSubmit}
                    primary
                    loading={userLoading}
                    disabled={userLoading}
                    flexible
                    centered
                />
            </DialogActions>
        </Dialog>
    )
})

export default ResetPasswordModal
